import type { AppStore } from "@/store/AppStore";
import type {
  AiToolkitAssistantProject,
  SearchAssistantProjectsInput,
  SearchAssistantProjectsOutput,
} from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  searchAssistantProjectsInputSchema,
  searchAssistantProjectsOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";
import {
  compareIsoStringsDescending,
  DEFAULT_AI_TOOLKIT_SEARCH_LIMIT,
  matchesAiToolkitSearchQuery,
  normalizeAiToolkitFilterValue,
  normalizeAiToolkitSearchQuery,
} from "@/modules/ai-toolkit/tools/utils/local-search-helpers";
import {
  buildAssistantProjectFromCreateChangeRequestUpdateValue,
  buildAssistantProjectFromSyncUpdateValue,
  isCreateProjectChangeRequestUpdateValue,
} from "@/modules/ai-toolkit/tools/assistant-projects/assistant-project-tools.helpers";

const matchesSearchInput = ({
  project,
  input,
  query,
}: {
  project: AiToolkitAssistantProject;
  input: SearchAssistantProjectsInput;
  query: string | undefined;
}): boolean => {
  const assistantId = normalizeAiToolkitFilterValue(input.assistantId);
  const status = normalizeAiToolkitFilterValue(input.status);

  if (assistantId && project.assistantId !== assistantId) return false;
  if (status && project.status !== status) return false;

  return matchesAiToolkitSearchQuery({
    query,
    values: [project.title, project.description],
  });
};

export const searchAssistantProjects = async ({
  store,
  input,
}: {
  store: AppStore;
  input: SearchAssistantProjectsInput;
}): Promise<SearchAssistantProjectsOutput> => {
  const query = normalizeAiToolkitSearchQuery(input.query);
  const [projectRows, changeRequestRows] = await Promise.all([
    store.assistantProjects.localTable.toArray(),
    store.assistantProjectChangeRequests.localTable.toArray(),
  ]);
  const realProjectIds = new Set(projectRows.map(projectRow => projectRow.model_id));

  const projects = [
    ...projectRows.map(projectRow =>
      buildAssistantProjectFromSyncUpdateValue({ updateValue: projectRow })
    ),
    ...changeRequestRows
      .filter(changeRequestRow => !realProjectIds.has(changeRequestRow.model_id))
      .filter(changeRequestRow =>
        isCreateProjectChangeRequestUpdateValue({ updateValue: changeRequestRow })
      )
      .map(changeRequestRow =>
        buildAssistantProjectFromCreateChangeRequestUpdateValue({
          updateValue: changeRequestRow,
        })
      ),
  ]
    .filter(project => matchesSearchInput({ project, input, query }))
    .sort(
      (left, right) =>
        compareIsoStringsDescending({
          left: left.lastActivityAt ?? left.updatedAt,
          right: right.lastActivityAt ?? right.updatedAt,
        }) || left.id.localeCompare(right.id)
    )
    .slice(0, input.limit ?? DEFAULT_AI_TOOLKIT_SEARCH_LIMIT);

  return { projects };
};

export const searchAssistantProjectsTool: AiToolkitToolDefinition<
  AiToolkitToolName.SearchAssistantProjects,
  SearchAssistantProjectsInput,
  SearchAssistantProjectsOutput
> = {
  name: AiToolkitToolName.SearchAssistantProjects,
  inputSchema: searchAssistantProjectsInputSchema,
  outputSchema: searchAssistantProjectsOutputSchema,
  strict: true,
  execute: searchAssistantProjects,
};
